import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getAgents, onboardAppPlayer } from '../services/api';
import { useApp } from '../context/AppContext';

const PLAYER_TYPES = [
  { id: 'development', label: 'Development' },
  { id: 'amateur', label: 'Amateur' },
  { id: 'professional', label: 'Professional' },
];

const playerTypeDescriptions = {
  development: 'Development player account. Coaches and organizers can support growth tracking.',
  amateur: 'Amateur player account. Scouts and agents can evaluate this player in the normal app flow.',
  professional: 'Professional player account. The server calculates ratings from professional football data under the hood.',
};

const EMPTY_FORM = {
  playerType: 'professional',
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  password: '',
  dateOfBirth: '',
  nationality: '',
  country: '',
  stateOfResidence: '',
  city: '',
  latitude: '',
  longitude: '',
  position: '',
  preferredFoot: 'right',
  height: '',
  weight: '',
  teamName: '',
  teamId: '',
  contractStatus: 'Free Agent',
  apiFootballPlayerId: '',
  apiFootballTeamId: '',
  apiFootballLeagueId: '',
  apiFootballSeason: new Date().getFullYear().toString(),
  apiFootballSearch: '',
  transfermarktUrl: '',
  highlightDescription: '',
  agentId: '',
};

function temporaryPassword() {
  const random = Math.random().toString(36).slice(2, 10);
  return `Tx-${random}`;
}

function appendIfPresent(formData, key, value) {
  if (value !== undefined && value !== null && String(value).trim() !== '') {
    formData.append(key, value);
  }
}

const inputStyle = { width: '100%' };

function Field({ label, children }) {
  return (
    <div>
      <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontWeight:500, fontSize:13, color:'var(--text-primary)', margin:'22px 0 12px', textTransform:'uppercase' }}>
      {children}
    </div>
  );
}

function UploadField({ label, accept, multiple, onChange, valueText }) {
  return (
    <Field label={label}>
      <label className="search-input" style={{ ...inputStyle, display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, cursor:'pointer' }}>
        <span style={{ color:valueText ? 'var(--text-primary)' : 'var(--text-dim)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {valueText || 'Choose file'}
        </span>
        <span className="btn btn-ghost" style={{ padding:'4px 10px', fontSize:11, flexShrink:0 }}>Browse</span>
        <input type="file" accept={accept} multiple={multiple} onChange={onChange} style={{ display:'none' }} />
      </label>
    </Field>
  );
}

function AgentPicker({ agents, loading, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef(null);
  const selected = agents.find(agent => agent.id === value) || null;
  const selectedName = selected
    ? selected.name || [selected.firstName, selected.lastName].filter(Boolean).join(' ') || selected.email
    : 'No agent';
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return agents;
    return agents.filter(agent => [agent.name, agent.firstName, agent.lastName, agent.email]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(term));
  }, [agents, search]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsideClick = event => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [open]);

  const choose = agentId => {
    onChange(agentId);
    setOpen(false);
    setSearch('');
  };

  return (
    <div ref={rootRef} style={{ position:'relative' }}>
      <button
        type="button"
        className="search-input"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={loading}
        onClick={() => setOpen(previous => !previous)}
        style={{ ...inputStyle, display:'flex', alignItems:'center', justifyContent:'space-between', textAlign:'left', cursor:loading ? 'default' : 'pointer' }}
      >
        <span>{loading ? 'Loading agents...' : selectedName}</span>
        <span aria-hidden="true" style={{ color:'var(--text-dim)' }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div role="listbox" style={{ position:'absolute', zIndex:50, top:'calc(100% + 5px)', left:0, right:0, maxHeight:330, overflowY:'auto', padding:8, border:'1px solid var(--border)', borderRadius:8, background:'var(--bg-card)', boxShadow:'0 16px 35px rgba(0,0,0,0.35)' }}>
          <input
            className="search-input"
            style={{ ...inputStyle, marginBottom:7, position:'sticky', top:0, zIndex:1 }}
            type="search"
            placeholder="Search agents by name or email"
            value={search}
            onChange={event => setSearch(event.target.value)}
            autoFocus
          />
          <button type="button" role="option" aria-selected={!value} onClick={() => choose('')} style={{ width:'100%', padding:'9px 10px', border:0, borderRadius:6, background:!value ? 'rgba(167,244,50,0.12)' : 'transparent', color:'var(--text-primary)', textAlign:'left', cursor:'pointer' }}>
            No agent <span style={{ color:'var(--text-dim)' }}>(optional)</span>
          </button>
          {filtered.map(agent => {
            const name = agent.name || [agent.firstName, agent.lastName].filter(Boolean).join(' ') || agent.email;
            return (
              <button key={agent.id} type="button" role="option" aria-selected={value === agent.id} onClick={() => choose(agent.id)} style={{ width:'100%', padding:'9px 10px', border:0, borderRadius:6, background:value === agent.id ? 'rgba(167,244,50,0.12)' : 'transparent', color:'var(--text-primary)', textAlign:'left', cursor:'pointer' }}>
                <span style={{ display:'block', fontSize:12 }}>{name}</span>
                {agent.email && <span style={{ display:'block', marginTop:2, fontSize:10, color:'var(--text-dim)' }}>{agent.email}</span>}
              </button>
            );
          })}
          {filtered.length === 0 && <div style={{ padding:'12px 10px', fontSize:11, color:'var(--text-dim)' }}>No matching agents found.</div>}
        </div>
      )}
    </div>
  );
}

export default function PlayerOnboarding() {
  const { auth, toast } = useApp();
  const [form, setForm] = useState({ ...EMPTY_FORM, password: temporaryPassword() });
  const [headshot, setHeadshot] = useState(null);
  const [reels, setReels] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);

  const fullName = useMemo(() => [form.firstName, form.lastName].filter(Boolean).join(' '), [form.firstName, form.lastName]);
  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  useEffect(() => {
    let cancelled = false;
    const loadAgents = async () => {
      try {
        const response = await getAgents(auth?.token, { limit: 100 });
        const rows = response?.data || [];
        if (!cancelled) setAgents(Array.isArray(rows) ? rows : []);
      } catch (error) {
        if (!cancelled) toast?.(error.message || 'Unable to load agents', 'error');
      } finally {
        if (!cancelled) setLoadingAgents(false);
      }
    };
    if (auth?.token) loadAgents();
    else setLoadingAgents(false);
    return () => { cancelled = true; };
  }, [auth?.token, toast]);

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setResult(null);

    try {
      const payload = { ...form };
      if (payload.playerType === 'professional') {
        payload.apiFootballSearch = payload.apiFootballSearch || fullName;
        payload.apiFootballSeason = payload.apiFootballSeason || new Date().getFullYear().toString();
      }

      const data = new FormData();
      Object.entries(payload).forEach(([key, value]) => appendIfPresent(data, key, value));
      if (headshot) data.append('headshot', headshot);
      reels.forEach(file => data.append('highlightReels', file));

      const response = await onboardAppPlayer(auth?.token, data);
      setResult(response.data || response);
      toast?.('Player onboarded successfully', 'success');
    } catch (error) {
      toast?.(error.message || 'Unable to onboard player', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:16, marginBottom:20 }}>
        <div>
          <h2 style={{ fontFamily:'var(--font-display)', fontSize:18, fontWeight:500, color:'var(--text-primary)', marginBottom:6 }}>Player onboarding</h2>
          <p style={{ color:'var(--text-muted)', fontSize:13, margin:0 }}>Create mobile player accounts with a temporary password, profile image, player card, and highlight reels.</p>
        </div>
        <button type="button" className="btn btn-ghost" onClick={() => update('password', temporaryPassword())}>
          Regenerate password
        </button>
      </div>

      <form className="card" onSubmit={submit} style={{ marginBottom:20, padding:'20px 24px' }}>
        <div style={{ fontWeight:500, fontSize:14, color:'var(--text-primary)', marginBottom:16 }}>CREATE PLAYER ACCOUNT</div>

        <div style={{ display:'grid', gridTemplateColumns:'repeat(3, minmax(0, 1fr))', gap:10, marginBottom:20 }}>
          {PLAYER_TYPES.map(type => (
            <button
              key={type.id}
              type="button"
              className={form.playerType === type.id ? 'btn btn-primary' : 'btn btn-ghost'}
              onClick={() => update('playerType', type.id)}
              style={{ justifyContent:'center' }}
            >
              {type.label}
            </button>
          ))}
        </div>

        <div style={{ background:'var(--bg-input)', borderRadius:6, padding:'8px 12px', fontSize:12, color:'var(--text-muted)', marginBottom:18 }}>
          <strong style={{ color:'var(--text-primary)' }}>{PLAYER_TYPES.find(type => type.id === form.playerType)?.label}</strong> — {playerTypeDescriptions[form.playerType]}
        </div>

        <SectionTitle>Account details</SectionTitle>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(2, minmax(0, 1fr))', gap:12, marginBottom:12 }}>
          <Field label="First name">
            <input className="search-input" style={inputStyle} value={form.firstName} onChange={e => update('firstName', e.target.value)} required />
          </Field>
          <Field label="Last name">
            <input className="search-input" style={inputStyle} value={form.lastName} onChange={e => update('lastName', e.target.value)} required />
          </Field>
          <Field label="Email">
            <input className="search-input" style={inputStyle} type="email" value={form.email} onChange={e => update('email', e.target.value)} required />
          </Field>
          <Field label="Temporary password">
            <input className="search-input" style={inputStyle} value={form.password} onChange={e => update('password', e.target.value)} required />
          </Field>
          <Field label="Phone">
            <input className="search-input" style={inputStyle} value={form.phoneNumber} onChange={e => update('phoneNumber', e.target.value)} />
          </Field>
          <Field label="Date of birth">
            <input className="search-input" style={inputStyle} type="date" value={form.dateOfBirth} onChange={e => update('dateOfBirth', e.target.value)} />
          </Field>
          <Field label="Link to agent (optional)">
            <AgentPicker agents={agents} loading={loadingAgents} value={form.agentId} onChange={agentId => update('agentId', agentId)} />
          </Field>
        </div>

        <div style={{ fontSize:12, color:'var(--text-dim)', margin:'8px 0 18px' }}>
          This password is only for first login. The player should change it from the app after signing in.
        </div>

        <SectionTitle>Location</SectionTitle>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(2, minmax(0, 1fr))', gap:12, marginBottom:18 }}>
          <Field label="Country">
            <input className="search-input" style={inputStyle} value={form.country} onChange={e => update('country', e.target.value)} />
          </Field>
          <Field label="State">
            <input className="search-input" style={inputStyle} value={form.stateOfResidence} onChange={e => update('stateOfResidence', e.target.value)} />
          </Field>
          <Field label="City">
            <input className="search-input" style={inputStyle} value={form.city} onChange={e => update('city', e.target.value)} />
          </Field>
          <Field label="Latitude">
            <input className="search-input" style={inputStyle} value={form.latitude} onChange={e => update('latitude', e.target.value)} />
          </Field>
          <Field label="Longitude">
            <input className="search-input" style={inputStyle} value={form.longitude} onChange={e => update('longitude', e.target.value)} />
          </Field>
          <Field label="Nationality">
            <input className="search-input" style={inputStyle} value={form.nationality} onChange={e => update('nationality', e.target.value)} />
          </Field>
        </div>

        <SectionTitle>Football profile</SectionTitle>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(2, minmax(0, 1fr))', gap:12, marginBottom:18 }}>
          <Field label="Position">
            <input className="search-input" style={inputStyle} value={form.position} onChange={e => update('position', e.target.value)} />
          </Field>
          <Field label="Preferred foot">
            <select className="search-input" style={inputStyle} value={form.preferredFoot} onChange={e => update('preferredFoot', e.target.value)}>
              <option value="right">Right</option>
              <option value="left">Left</option>
            </select>
          </Field>
          <Field label="Contract">
            <select className="search-input" style={inputStyle} value={form.contractStatus} onChange={e => update('contractStatus', e.target.value)}>
              <option>Free Agent</option>
              <option>Under Contract</option>
              <option>On Loan</option>
            </select>
          </Field>
          <Field label="Height">
            <input className="search-input" style={inputStyle} value={form.height} onChange={e => update('height', e.target.value)} />
          </Field>
          <Field label="Weight">
            <input className="search-input" style={inputStyle} value={form.weight} onChange={e => update('weight', e.target.value)} />
          </Field>
          <Field label="Team">
            <input className="search-input" style={inputStyle} value={form.teamName} onChange={e => update('teamName', e.target.value)} />
          </Field>
          <Field label="Player profile link (optional)">
            <input
              className="search-input"
              style={inputStyle}
              type="url"
              placeholder="Transfermarkt or another public profile URL"
              value={form.transfermarktUrl}
              onChange={e => update('transfermarktUrl', e.target.value)}
            />
          </Field>
        </div>

        <div style={{ fontSize:11, color:'var(--text-dim)', margin:'-8px 0 18px' }}>
          Optional. Add a Transfermarkt link or another public page that showcases the player profile.
        </div>

        <SectionTitle>Assets</SectionTitle>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(2, minmax(0, 1fr))', gap:12, marginBottom:18 }}>
          <UploadField
            label="Headshot"
            accept="image/*"
            valueText={headshot?.name}
            onChange={e => setHeadshot(e.target.files?.[0] || null)}
          />
          <UploadField
            label="Highlight reels"
            accept="video/*"
            multiple
            valueText={reels.length ? `${reels.length} reel${reels.length === 1 ? '' : 's'} selected` : ''}
            onChange={e => setReels(Array.from(e.target.files || []))}
          />
        </div>

        <Field label="Highlight note">
          <textarea className="search-input" style={{ ...inputStyle, minHeight:80, resize:'vertical' }} rows="3" value={form.highlightDescription} onChange={e => update('highlightDescription', e.target.value)} />
        </Field>

        <input type="hidden" value={form.apiFootballSeason} readOnly />
        <input type="hidden" value={form.apiFootballSearch || fullName} readOnly />

        <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:20 }}>
          <button type="button" className="btn btn-ghost" onClick={() => {
            setForm({ ...EMPTY_FORM, password: temporaryPassword() });
            setHeadshot(null);
            setReels([]);
            setResult(null);
          }}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Creating...' : 'Create player'}
          </button>
        </div>
      </form>

      {result && (
        <section className="card" style={{ padding:0 }}>
          <div className="card-header" style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)' }}>
            <span className="card-title">CREATED PLAYER ACCOUNT</span>
          </div>
          <div className="table-wrap">
            <table>
              <tbody>
                <tr><th>Player ID</th><td>{result.playerId || result.id}</td></tr>
                <tr><th>Email</th><td>{result.email}</td></tr>
                <tr><th>Temporary password</th><td>{result.temporaryPassword}</td></tr>
                <tr><th>Type</th><td>{result.playerType}</td></tr>
                <tr><th>Linked agent</th><td>{result.agent?.name || result.agent?.email || 'None'}</td></tr>
                <tr><th>Player card</th><td>{result.playerCardUrl ? <a href={result.playerCardUrl} target="_blank" rel="noreferrer">Open card</a> : '-'}</td></tr>
                <tr><th>Reels</th><td>{Array.isArray(result.highlightReels) ? result.highlightReels.length : 0}</td></tr>
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
